import { faEnvelope } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	ApiService,
	isInAppComposeAvailable,
	openInAppCompose,
	usePreferencesStore,
} from '@supply-mover-2-0/core';
import React, { useCallback, useState } from 'react';
import { useThemeColors } from '../../providers/ThemeProvider';
import { useTranslation } from '../../providers/TranslationProvider';
import type { EmailMethodType } from './EmailMethodModal';
import { EmailMethodModal } from './EmailMethodModal';

// Cached once per session: does the current user have an active email
// connection? Fetched lazily on the first click (not per render) so a table of
// hundreds of email links triggers at most one request.
let cachedHasConnection: Promise<boolean> | null = null;

function getHasEmailConnection(): Promise<boolean> {
	if (cachedHasConnection == null) {
		cachedHasConnection = ApiService.apiGet<{
			connections?: Array<{ isActive?: boolean }>;
		}>('/email/connections')
			.then((res) =>
				Array.isArray(res.connections)
					? res.connections.some((c) => c.isActive === true)
					: false,
			)
			.catch(() => false);
	}

	return cachedHasConnection;
}

interface ClickToEmailProps {
	email: string | null | undefined;
	className?: string;
	showIcon?: boolean;
}

/**
 * Email address with a click-to-send chooser.
 *
 * If the user has a connected email account (and an in-app composer is
 * available), clicking offers a choice between opening the OS mail app
 * (`mailto:`) and composing in-app — remembering the choice when asked.
 * Otherwise it behaves like a plain `mailto:` link.
 */
export function ClickToEmail({
	email,
	className = '',
	showIcon = false,
}: ClickToEmailProps): React.ReactElement {
	const { t } = useTranslation();
	const colors = useThemeColors();
	const [showMethodModal, setShowMethodModal] = useState(false);

	const trimmedEmail = email?.trim() ?? '';

	const openMailto = useCallback((): void => {
		window.location.href = `mailto:${trimmedEmail}`;
	}, [trimmedEmail]);

	const doCompose = useCallback((): void => {
		// Falls back to mailto if no composer host is mounted.
		if (!openInAppCompose({ to: trimmedEmail })) {
			openMailto();
		}
	}, [trimmedEmail, openMailto]);

	const applyMethod = useCallback(
		(method: EmailMethodType): void => {
			if (method === 'compose') {
				doCompose();
			} else {
				openMailto();
			}
		},
		[doCompose, openMailto],
	);

	const handleClick = useCallback(
		async (e: React.MouseEvent): Promise<void> => {
			e.preventDefault();
			e.stopPropagation();

			const canCompose =
				isInAppComposeAvailable() && (await getHasEmailConnection());

			// No in-app option available — behave exactly like a mailto link.
			if (!canCompose) {
				openMailto();
				return;
			}

			const preference =
				usePreferencesStore.getState().email.sendMethodPreference;

			if (preference === 'mailto' || preference === 'compose') {
				applyMethod(preference);
				return;
			}

			// No saved preference — ask.
			setShowMethodModal(true);
		},
		[openMailto, applyMethod],
	);

	const handleSelectMethod = useCallback(
		(method: EmailMethodType, remember: boolean): void => {
			if (remember) {
				usePreferencesStore
					.getState()
					.setEmailPreference('sendMethodPreference', method);
			}
			applyMethod(method);
		},
		[applyMethod],
	);

	if (trimmedEmail === '') {
		return (
			<span
				className={className}
				style={{ color: colors.values.textSecondary }}
			>
				{t('-')}
			</span>
		);
	}

	return (
		<>
			<a
				href={`mailto:${trimmedEmail}`}
				onClick={(e) => {
					void handleClick(e);
				}}
				className={`hover:underline transition-all inline-flex items-center gap-1 ${className}`}
				style={{ color: colors.values.primary }}
				title={t('Click to send email')}
			>
				{showIcon ? (
					<FontAwesomeIcon icon={faEnvelope} className="text-xs" />
				) : null}
				{t(trimmedEmail)}
			</a>

			<EmailMethodModal
				isOpen={showMethodModal}
				onClose={() => {
					setShowMethodModal(false);
				}}
				email={trimmedEmail}
				onSelectMethod={handleSelectMethod}
			/>
		</>
	);
}
